// src/components/shared/AssetGrid.js
import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import AssetCard from "./AssetCard";
import Focusable from "../common/Focusable";

export default function AssetGrid({
  source,
  query,
  collectionId,
  loader,               // REQUIRED: async () => items[]
  onEnter,
  onFocusItem,
  onItemsLoaded,
  cols = 5,
  anchorOffsetPx = 30,
  gapPx = 16,
  cardWidthPx = 360,
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!loader) {
          // Keep a clear warning to catch accidental omissions in dev.
          // eslint-disable-next-line no-console
          console.warn("AssetGrid: loader prop is required; returning empty list.");
          if (!cancelled) {
            setItems([]);
            onItemsLoaded?.([]);
          }
          return;
        }
        const data = await loader();
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setItems(list);
          onItemsLoaded?.(list);
          if (list.length > 0) onFocusItem?.(list[0]);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("AssetGrid load error:", e);
        if (!cancelled) {
          setItems([]);
          onItemsLoaded?.([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, query, collectionId, loader, onFocusItem, onItemsLoaded]);

  const CARD_W = cardWidthPx;
  const CARD_H = Math.round((CARD_W * 9) / 16);
  const GAP = gapPx;
  const ROW_STRIDE = CARD_H + GAP;
  const VIEW_ROWS = 4;
  const VIEW_H = ROW_STRIDE * VIEW_ROWS;

  const rows = useMemo(() => chunk(items, cols), [items, cols]);
  const rowCount = rows.length;
  const contentWidth = cols * CARD_W + (cols - 1) * GAP;

  const [focusedRow, setFocusedRow] = useState(0);
  const clampedFocusedRow = clamp(focusedRow, 0, Math.max(0, rowCount - 1));

  return (
    <Box
      sx={{
        position: "relative",
        height: `${VIEW_H}px`,
        width: "100%",
        overflow: "hidden",
        mt: `calc(30vh + ${anchorOffsetPx}px)`,
        zIndex: 900,
        contain: "layout paint",
        transform: "translateZ(0)",
      }}
      data-rails-root
    >
      {rows.map((rowItems, rowIndex) => {
        const distance = rowIndex - clampedFocusedRow;
        return (
          <Box
            key={`row-${rowIndex}`}
            sx={{
              position: "absolute",
              left: "50%",
              transform: `translate3d(-50%, ${distance * ROW_STRIDE}px, 0)`,
              width: `${contentWidth}px`,
              opacity: distance < 0 ? 0 : 1,
              transition: "transform 320ms ease, opacity 160ms ease",
              willChange: "transform, opacity",
            }}
          >
            <RowGrid
              items={rowItems}
              rowIndex={rowIndex}
              cols={cols}
              cardW={CARD_W}
              cardH={CARD_H}
              gap={GAP}
              onFocusRow={setFocusedRow}
              onEnter={onEnter}
              onFocusItem={onFocusItem}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function RowGrid({
  items,
  rowIndex,
  cols,
  cardW,
  cardH,
  gap,
  onFocusRow,
  onEnter,
  onFocusItem,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${cardW}px)`,
        columnGap: `${gap}px`,
        rowGap: 0,
        alignItems: "start",
      }}
    >
      {items.map((item, colIndex) => {
        const autoFocus = rowIndex === 0 && colIndex === 0;
        const handleFocus = () => {
          onFocusRow?.(rowIndex);
          onFocusItem?.(item);
        };
        return (
          <Focusable
            key={item.id ?? `${rowIndex}-${colIndex}`}
            row={rowIndex}
            col={colIndex}
            autoFocus={autoFocus}
            onFocus={handleFocus}
            onClick={() => onEnter?.(item)}
            data-click-target="[data-card-click]"
            sx={{ "&:focus, &:focus-visible": { outline: "none" } }}
          >
            <Box sx={{ width: cardW, height: cardH }}>
              <AssetCard item={item} onEnter={() => onEnter?.(item)} />
            </Box>
          </Focusable>
        );
      })}
    </Box>
  );
}

function clamp(n, min, max) {
  const v = Number.isFinite(n) ? Math.floor(n) : 0;
  return Math.min(Math.max(v, min), max);
}
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
